import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PremiumPaymentModal from "@/components/app/PremiumPaymentModal";
import { Sparkles, Check, ChevronDown, ChevronUp, Menu, X, ArrowRight } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Mobile menu & Modal states
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("tab-dashboard");
  const [openFaqId, setOpenFaqId] = useState(1);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const toggleFaq = (id) => {
    setOpenFaqId((prev) => (prev === id ? null : id));
  };

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleStartTrial = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/register");
    }
  };

  const handleBuyPremium = () => {
    if (user) {
      setPaymentModalOpen(true);
    } else {
      navigate("/register?plan=premium");
    }
  };

  return (
    <div className="bg-[#FFFCF9] text-[#35282C] font-sans antialiased overflow-x-hidden selection:bg-[#F9EFF0] selection:text-[#9E4053]">
      {/* ==================== 1. NAVIGATION HEADER ==================== */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FFFCF9]/90 border-b border-[#BE5366]/10 transition-all duration-300">
        <nav aria-label="Main Navigation" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          {/* Logo (tanpa teks Weddly, logo diperbesar) */}
          <a
            onClick={(e) => scrollToSection(e, "beranda")}
            className="flex items-center gap-3 group cursor-pointer"
            href="#beranda"
          >
            <img
              src="/weddly-logo.png"
              alt="Weddly Logo"
              className="w-12 h-12 md:w-14 md:h-14 object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          {/* Desktop Nav Links dengan Animasi Smooth Scroll */}
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-[#90777D]">
            <a onClick={(e) => scrollToSection(e, "beranda")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#beranda">Beranda</a>
            <a onClick={(e) => scrollToSection(e, "keunggulan")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#keunggulan">Keunggulan</a>
            <a onClick={(e) => scrollToSection(e, "fitur")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#fitur">Fitur</a>
            <a onClick={(e) => scrollToSection(e, "cara-kerja")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#cara-kerja">Cara Kerja</a>
            <a onClick={(e) => scrollToSection(e, "harga")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#harga">Harga</a>
            <a onClick={(e) => scrollToSection(e, "faq")} className="hover:text-[#BE5366] transition-colors duration-200 cursor-pointer" href="#faq">FAQ</a>
          </div>

          {/* Action Buttons Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-md hover:shadow-floating transition-all duration-200 transform hover:-translate-y-0.5"
              >
                Buka Dashboard →
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-[#35282C] hover:text-[#BE5366] transition-colors px-3 py-2"
                >
                  Masuk
                </button>
                <button
                  onClick={handleStartTrial}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-md hover:shadow-floating transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Coba Gratis 14 Hari
                </button>
              </>
            )}
          </div>

          {/* Mobile Hamburger Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-[#35282C] hover:bg-[#F9EFF0] focus:outline-none"
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#FFFCF9] border-b border-[#BE5366]/10 px-4 pt-3 pb-6 space-y-3">
            <a onClick={(e) => scrollToSection(e, "beranda")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#beranda">Beranda</a>
            <a onClick={(e) => scrollToSection(e, "keunggulan")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#keunggulan">Keunggulan</a>
            <a onClick={(e) => scrollToSection(e, "fitur")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#fitur">Fitur</a>
            <a onClick={(e) => scrollToSection(e, "cara-kerja")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#cara-kerja">Cara Kerja</a>
            <a onClick={(e) => scrollToSection(e, "harga")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#harga">Harga</a>
            <a onClick={(e) => scrollToSection(e, "faq")} className="block px-3 py-2 text-base font-medium text-[#35282C] rounded-lg hover:bg-[#F9EFF0]" href="#faq">FAQ</a>
            <div className="pt-3 border-t border-[#BE5366]/10 flex flex-col gap-2">
              {user ? (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="text-center py-2.5 font-semibold text-white bg-[#BE5366] rounded-full shadow-md"
                >
                  Buka Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate("/login")}
                    className="text-center py-2.5 font-semibold text-[#35282C] border border-[#35282C]/20 rounded-full"
                  >
                    Masuk
                  </button>
                  <button
                    onClick={handleStartTrial}
                    className="text-center py-2.5 font-semibold text-white bg-[#BE5366] rounded-full shadow-md"
                  >
                    Coba Gratis 14 Hari
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* ==================== 2. HERO SECTION ==================== */}
      <section className="relative pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden hero-glow" id="beranda">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Hero Text */}
            <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F9EFF0] border border-[#BE5366]/20 text-xs sm:text-sm font-semibold text-[#9E4053] tracking-wide">
                <span className="text-[#BE5366]">✦</span> Introducing Weddly Wedding Planner
              </div>
              <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#35282C] leading-[1.15]">
                Wedding Planning Made <span class="italic font-normal text-[#BE5366]">Beautifully Simple.</span>
              </h1>
              <p className="text-base sm:text-lg text-[#90777D] leading-relaxed max-w-xl mx-auto lg:mx-0">
                Dari menyusun anggaran hingga mengirim undangan, semua persiapan pernikahan kini lebih mudah bersama Weddly. Kelola setiap detail hari bahagiamu dalam satu platform digital yang praktis, elegan, dan terorganisasi.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  onClick={handleStartTrial}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full text-base font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-lg hover:shadow-floating transition-all duration-200 transform hover:-translate-y-0.5 text-center"
                >
                  Coba Gratis 14 Hari
                </button>
                <a
                  onClick={(e) => scrollToSection(e, "fitur")}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full text-base font-semibold text-[#35282C] border border-[#35282C]/20 hover:border-[#BE5366] hover:text-[#BE5366] hover:bg-white/60 transition-all duration-200 text-center cursor-pointer"
                  href="#fitur"
                >
                  Jelajahi Fitur
                </a>
              </div>
              <p className="text-xs sm:text-sm text-[#90777D]/80 pt-1 flex items-center justify-center lg:justify-start gap-2 flex-wrap">
                <span>✓ Mulai gratis 14 hari</span>
                <span className="text-[#D3B68C]">•</span>
                <span>Tanpa kartu kredit</span>
                <span className="text-[#D3B68C]">•</span>
                <span>Upgrade Premium kapan saja</span>
              </p>
            </div>

            {/* Right Hero Visual Mockup */}
            <div className="lg:col-span-6 relative">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#D3B68C]/15 rounded-full filter blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#F9EFF0] rounded-full filter blur-2xl pointer-events-none"></div>

              <div className="relative mx-auto max-w-lg lg:max-w-none">
                <div className="rounded-2xl p-2 bg-gradient-to-b from-white/90 to-[#F9EFF0]/40 shadow-2xl border border-[#D3B68C]/30">
                  <div className="bg-[#35282C]/5 rounded-t-xl px-4 py-2.5 flex items-center gap-2 border-b border-[#35282C]/5">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
                      <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span>
                      <span className="w-3 h-3 rounded-full bg-green-400 inline-block"></span>
                    </div>
                    <div className="mx-auto bg-white/70 text-[11px] font-medium text-[#90777D] px-6 py-0.5 rounded-md border border-[#BE5366]/10 truncate max-w-[200px]">
                      app.weddly.id/dashboard
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-b-xl bg-white relative">
                    <img
                      alt="Weddly Dashboard Mockup"
                      className="w-full h-auto object-cover block"
                      src="/landing-hero.png"
                    />
                  </div>
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-6 -left-4 sm:-left-8 glass-card p-3 sm:p-4 rounded-xl shadow-luxury flex items-center gap-3 hidden sm:flex">
                  <div className="w-9 h-9 rounded-full bg-[#738A78]/20 text-[#738A78] flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#90777D] font-medium">Checklist Status</p>
                    <p className="text-xs font-bold text-[#35282C]">Venue Booked ✓, Catering 85%</p>
                  </div>
                </div>

                <div className="absolute -bottom-8 -right-2 sm:-right-6 glass-card p-3 sm:p-4 rounded-xl shadow-luxury flex items-center gap-3 border-l-4 border-[#D3B68C]">
                  <div className="w-9 h-9 rounded-full bg-[#D3B68C]/20 text-[#35282C] flex items-center justify-center text-sm font-semibold">
                    Rp
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[#90777D] font-medium">Total Anggaran: Rp125.000.000</p>
                    <p className="text-xs font-bold text-[#738A78]">Hemat Rp14.200.000 (11%)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 3. PROBLEM & SOLUTION ==================== */}
      <section className="py-20 bg-white border-y border-[#BE5366]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#BE5366]">Tantangan Klasik Pernikahan</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Persiapan Pernikahan Tidak Harus Serumit Itu.
            </h2>
            <p class="text-base text-[#90777D] mt-3">
              Seringkali pasangan merasa kewalahan bukan karena acaranya, melainkan karena data yang tercecer di mana-mana.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-[#F9EFF0]/60 border border-[#BE5366]/15 hover:border-[#BE5366]/40 transition-all duration-200">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-lg mb-4">✕</div>
              <h3 className="font-bold text-base text-[#35282C] mb-2">Checklist Tersebar</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">Tercatat di berbagai notes HP, buku tulis, dan obrolan chat WhatsApp yang tenggelam.</p>
            </div>

            <div className="p-6 rounded-2xl bg-[#F9EFF0]/60 border border-[#BE5366]/15 hover:border-[#BE5366]/40 transition-all duration-200">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-lg mb-4">✕</div>
              <h3 className="font-bold text-base text-[#35282C] mb-2">Anggaran Bocor Halus</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">Pengeluaran tak terduga sulit dipantau, menyebabkan over-budget tanpa disadari sejak awal.</p>
            </div>

            <div className="p-6 rounded-2xl bg-[#F9EFF0]/60 border border-[#BE5366]/15 hover:border-[#BE5366]/40 transition-all duration-200">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-lg mb-4">✕</div>
              <h3 className="font-bold text-base text-[#35282C] mb-2">Inspirasi Berantakan</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">Tangkapan layar gaun, dekorasi bunga, & katering menumpuk tidak terorganisir di galeri.</p>
            </div>

            <div className="p-6 rounded-2xl bg-[#F9EFF0]/60 border border-[#BE5366]/15 hover:border-[#BE5366]/40 transition-all duration-200">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-lg mb-4">✕</div>
              <h3 className="font-bold text-base text-[#35282C] mb-2">Miskomunikasi Pasangan</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">Daftar tamu, vendor, dan rundown tidak ter-sinkronisasi realtime antara pengantin pria dan wanita.</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-[#F9EFF0] via-white to-[#FFFCF9] border border-[#D3B68C]/40 shadow-luxury mt-12">
            <div className="text-center space-y-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#738A78]/20 text-[#738A78]">
                Solusi Cerdas Weddly
              </span>
              <h3 className="font-serif-title text-2xl sm:text-3xl lg:text-4xl font-bold text-[#35282C]">
                Satu Workspace untuk Semua Persiapanmu.
              </h3>
              <p className="text-[#90777D] text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                Weddly menyatukan checklist, anggaran, buku tamu, rundown, dan komunikasi vendor dalam satu portal terpadu. Kolaborasi real-time antara kamu, pasangan, keluarga, dan wedding planner dalam satu sentuhan.
              </p>
              <div className="pt-4 flex flex-wrap justify-center gap-3 text-xs font-semibold text-[#35282C]">
                <span className="px-4 py-2 rounded-xl bg-white shadow-sm border border-[#D3B68C]/20">✨ Real-time Multi-device Sync</span>
                <span className="px-4 py-2 rounded-xl bg-white shadow-sm border border-[#D3B68C]/20">💍 Akses Bersama Pasangan</span>
                <span className="px-4 py-2 rounded-xl bg-white shadow-sm border border-[#D3B68C]/20">📱 Mobile & Tablet Friendly</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 4. CORE PRODUCT BENEFITS ==================== */}
      <section className="py-24 bg-[#FFFCF9]" id="keunggulan">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#B89768]">Keunggulan Eksklusif</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Rancang Pernikahan Impian Tanpa Beban Stres.
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              Platform modern yang didesain estetik dan fungsional agar proses perencanaan menjadi momen yang tak terlupakan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-white border border-[#D3B68C]/30 hover:shadow-luxury transition-all duration-300 relative group overflow-hidden">
              <div className="w-12 h-12 rounded-xl bg-[#F9EFF0] text-[#BE5366] flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform">📂</div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">1. Everything in One Place</h3>
              <p className="text-sm sm:text-base text-[#90777D] leading-relaxed">
                Semua kebutuhan tersusun rapi dalam satu dashboard terpusat. Tak ada lagi file Excel yang terpisah atau invoice vendor yang hilang saat dibutuhkan di hari H.
              </p>
              <div className="mt-6 pt-4 border-t border-[#F9EFF0] flex items-center text-xs font-semibold text-[#B89768]">
                Sentralisasi data 100% aman & terstruktur
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-white border border-[#D3B68C]/30 hover:shadow-luxury transition-all duration-300 relative group overflow-hidden">
              <div className="w-12 h-12 rounded-xl bg-[#F9EFF0] text-[#BE5366] flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform">💰</div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">2. Stay on Budget</h3>
              <p className="text-sm sm:text-base text-[#90777D] leading-relaxed">
                Pantau estimasi biaya, realisasi pengeluaran, termin DP vendor, dan target tabungan secara real-time. Dapatkan kalkulasi otomatis agar keuangan pernikahanmu tetap sehat.
              </p>
              <div className="mt-6 pt-4 border-t border-[#F9EFF0] flex items-center text-xs font-semibold text-[#B89768]">
                Transparansi budget bersama pasangan
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-white border border-[#D3B68C]/30 hover:shadow-luxury transition-all duration-300 relative group overflow-hidden">
              <div className="w-12 h-12 rounded-xl bg-[#F9EFF0] text-[#BE5366] flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform">🎨</div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">3. Create Your Dream Wedding</h3>
              <p className="text-sm sm:text-base text-[#90777D] leading-relaxed">
                Kurasi inspirasi moodboard, palet warna HEX, referensi busana pengantin, hingga tata letak dekorasi bunga untuk dikomunikasikan secara presisi ke vendor dekorasi.
              </p>
              <div className="mt-6 pt-4 border-t border-[#F9EFF0] flex items-center text-xs font-semibold text-[#B89768]">
                Ekspresikan estetika konsep pernikahanmu
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-white border border-[#D3B68C]/30 hover:shadow-luxury transition-all duration-300 relative group overflow-hidden">
              <div className="w-12 h-12 rounded-xl bg-[#F9EFF0] text-[#BE5366] flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform">✨</div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">4. Plan Smarter with AI</h3>
              <p className="text-sm sm:text-base text-[#90777D] leading-relaxed">
                Weddly AI asisten pintar siap merekomendasikan tema intimate, susunan rundown menit-ke-menit, hingga rincian porsi katering sesuai jumlah tamu yang kamu undang.
              </p>
              <div className="mt-6 pt-4 border-t border-[#F9EFF0] flex items-center text-xs font-semibold text-[#B89768]">
                Kecerdasan buatan khusus wedding planner
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 5. COMPLETE FEATURE SHOWCASE (BENTO GRID) ==================== */}
      <section className="py-24 bg-white border-t border-[#BE5366]/10" id="fitur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#BE5366]">Fitur Komprehensif</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Semua yang Kamu Butuhkan untuk Hari Bahagiamu.
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              13 modul canggih dirancang khusus untuk kenyamanan dan ketenangan pikiran calon pengantin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Module 1 */}
            <div className="md:col-span-2 p-6 sm:p-8 rounded-3xl bg-[#F9EFF0]/40 border border-[#BE5366]/20 hover:border-[#BE5366] transition-all flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#BE5366] text-white">Modul 01</span>
                  <span className="text-xs text-[#90777D] font-semibold">Live Overview</span>
                </div>
                <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-2">Executive Wedding Dashboard</h3>
                <p className="text-sm text-[#90777D] mb-6">
                  Pusat kendali komprehensif yang merangkum hitung mundur H-Day, progres checklist keseluruhan, ringkasan sisa anggaran, dan aktivitas terbaru.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-[#BE5366]/10 space-y-2">
                <div className="flex justify-between text-xs font-semibold text-[#35282C]">
                  <span>Kesiapan Pernikahan Amelia & James</span>
                  <span className="text-[#BE5366]">78%</span>
                </div>
                <div className="w-full bg-[#F9EFF0] h-2.5 rounded-full overflow-hidden">
                  <div className="bg-[#BE5366] h-2.5 rounded-full" style={{ width: "78%" }}></div>
                </div>
              </div>
            </div>

            {/* Module 2 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 02</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Undangan Digital</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Buat website undangan elegan dengan live preview, galeri foto cinta, pemutar musik background, dan RSVP tracker terintegrasi.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#BE5366] font-semibold">
                Lihat Sample Web Undangan →
              </div>
            </div>

            {/* Module 3 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 03</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Hantaran & Mahar</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Katalogisasi daftar seserahan tradisi adat, status pembelian box hantaran, nominal mahar, dan penanggung jawab perlengkapan.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#738A78] font-semibold">
                Status: 12/12 Box Siap Hias
              </div>
            </div>

            {/* Module 4 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 04</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Checklist Interaktif</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Daftar tugas terstruktur dari H-365 hingga hari H. Dilengkapi reminder otomatis agar tidak ada kewajiban dokumen KUA yang tertinggal.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#90777D]">
                Preset adat & modern internasional
              </div>
            </div>

            {/* Module 5 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 05</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Anggaran Terpadu</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Kelola pos alokasi venue, catering, busana, MUA, dan dokumentasi foto. Hitung otomatis selisih estimasi dengan realisasi pembayaran.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#BE5366] font-semibold">
                Auto Currency Format (IDR)
              </div>
            </div>

            {/* Module 6 */}
            <div className="md:col-span-2 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#F9EFF0]/60 to-[#FFFCF9] border border-[#D3B68C]/30 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#B89768] text-white">Modul 06</span>
                  <span className="text-xs font-bold text-[#738A78]">Simulasi Tabungan Kolaboratif</span>
                </div>
                <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-2">Tabungan Bersama Pasangan</h3>
                <p className="text-sm text-[#90777D] leading-relaxed">
                  Tetapkan target tabungan bulanan antara pengantin pria dan wanita. Pantau progres akumulasi dana pernikahan secara transparan.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4 bg-white/80 p-4 rounded-xl border border-[#D3B68C]/20">
                <div>
                  <p className="text-[11px] text-[#90777D]">Terkumpul:</p>
                  <p className="text-sm font-bold text-[#35282C]">Rp95.000.000</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#90777D]">Target Akhir:</p>
                  <p className="text-sm font-bold text-[#35282C]">Rp120.000.000 (79%)</p>
                </div>
              </div>
            </div>

            {/* Module 7 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 07</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Buku Tamu Digital</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Kategorisasi tamu VIP, keluarga, rekan kantor, status pengiriman undangan cetak, dan konfirmasi WhatsApp terhubung.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#90777D]">
                QR Code Check-in ready
              </div>
            </div>

            {/* Module 8 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 08</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Wedding Moodboard</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Kanvas visual untuk menyematkan palet warna hex, gaya kebaya & gaun pengantin, hingga dekorasi pelaminan modern.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full bg-[#BE5366] inline-block"></span>
                <span className="w-4 h-4 rounded-full bg-[#D3B68C] inline-block"></span>
                <span className="w-4 h-4 rounded-full bg-[#738A78] inline-block"></span>
                <span className="text-[11px] text-[#90777D] ml-1">Palet Tersimpan</span>
              </div>
            </div>

            {/* Module 9 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 09</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Direktori Vendor</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Simpan kontak PIC, nomor WhatsApp, kontrak kerjasama, bukti transfer DP, dan jadwal fitting busana dalam satu tabel teratur.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#738A78] font-semibold">
                6 Vendor Terkonfirmasi
              </div>
            </div>

            {/* Module 10 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 10</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Jadwal Rundown</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Jadwal menit-demi-menit untuk akad nikah, resepsi, temu manten, hingga sesi foto bersama. Siap di-export PDF untuk panitia.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#BE5366] font-semibold">
                Format Cetak PDF & Excel
              </div>
            </div>

            {/* Module 11 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 11</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Brankas Dokumen</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Penyimpanan aman berbasis cloud terenkripsi untuk KTP, surat rekomendasi KUA/Gereja, kartu keluarga, dan MoU vendor resmi.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#90777D]">
                Enkripsi Cloud 256-bit
              </div>
            </div>

            {/* Module 12 */}
            <div className="p-6 rounded-3xl bg-white border border-[#D3B68C]/30 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D3B68C]/30 text-[#35282C] inline-block mb-4">Modul 12</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Honeymoon Planner</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Perencanaan liburan bulan madu komprehensif dari pencatatan estimasi anggaran, jadwal itinerary harian, hingga checklist persiapan koper.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F9EFF0] text-xs text-[#738A78] font-semibold">
                Rencana & Itinerary Terintegrasi
              </div>
            </div>

            {/* Module 13 */}
            <div className="p-6 rounded-3xl bg-[#F9EFF0] border border-[#BE5366]/40 hover:border-[#BE5366] transition-all flex flex-col justify-between shadow-sm">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#BE5366] text-white inline-block mb-4">Modul 13</span>
                <h3 className="font-bold text-lg text-[#35282C] mb-2">Weddly AI Assistant</h3>
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Konsultan virtual pintar untuk merumuskan konsep resepsi intimate, rekomendasi rundown waktu, dan kalkulator porsi konsumsi katering.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-[#BE5366]/20 text-xs text-[#9E4053] font-bold">
                ✦ AI Wedding Concierge
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 6. INTERACTIVE PRODUCT PREVIEW ==================== */}
      <section className="py-24 bg-[#F9EFF0]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#BE5366]">Simulasi Interaktif</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Lihat Bagaimana Weddly Membantu Persiapanmu.
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              Jelajahi antarmuka intuitif aplikasi Weddly langsung dari perambanmu.
            </p>
          </div>

          {/* Tab buttons */}
          <div className="flex items-center justify-center flex-wrap gap-2 sm:gap-3 mb-10">
            {[
              { id: "tab-dashboard", label: "Dashboard" },
              { id: "tab-anggaran", label: "Anggaran" },
              { id: "tab-undangan", label: "Undangan Digital" },
              { id: "tab-moodboard", label: "Wedding Moodboard" },
              { id: "tab-checklist", label: "Checklist" },
              { id: "tab-honeymoon", label: "Honeymoon Planner" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  activeTab === t.id
                    ? "bg-[#BE5366] text-white shadow-md"
                    : "bg-white text-[#35282C] hover:bg-[#F9EFF0] border border-[#BE5366]/15"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Window Content */}
          <div className="max-w-5xl mx-auto rounded-3xl bg-white border border-[#D3B68C]/40 shadow-luxury overflow-hidden">
            <div className="bg-[#FFFCF9] border-b border-[#D3B68C]/20 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#BE5366]/60 inline-block"></span>
                <span className="w-3 h-3 rounded-full bg-[#D3B68C] inline-block"></span>
                <span className="w-3 h-3 rounded-full bg-[#738A78] inline-block"></span>
                <span className="text-xs font-semibold text-[#90777D] ml-3">Weddly Cloud Suite 2.0</span>
              </div>
              <div className="text-xs text-[#90777D] font-medium hidden sm:block">
                Status: <span className="text-[#738A78] font-bold">● Terhubung & Realtime</span>
              </div>
            </div>

            {/* Tab 1: Dashboard */}
            {activeTab === "tab-dashboard" && (
              <div className="p-6 sm:p-8 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-[#F9EFF0]/50 border border-[#BE5366]/20">
                    <span className="text-xs text-[#90777D]">Hari Bahagia</span>
                    <p className="text-2xl font-serif-title font-bold text-[#35282C] mt-1">H-84 Hari</p>
                    <p className="text-xs text-[#BE5366] mt-1">Sabtu, 14 Juni 2025</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[#F9EFF0]/50 border border-[#BE5366]/20">
                    <span className="text-xs text-[#90777D]">Total Terpakai</span>
                    <p className="text-2xl font-serif-title font-bold text-[#35282C] mt-1">Rp86.400.000</p>
                    <p className="text-xs text-[#738A78] mt-1">Dari plafon Rp130.000.000</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[#F9EFF0]/50 border border-[#BE5366]/20">
                    <span className="text-xs text-[#90777D]">RSVP Masuk</span>
                    <p className="text-2xl font-serif-title font-bold text-[#35282C] mt-1">285 / 350</p>
                    <p className="text-xs text-[#90777D] mt-1">81% Telah Konfirmasi</p>
                  </div>
                </div>
                <div className="p-5 rounded-2xl border border-[#D3B68C]/30 bg-[#FFFCF9]/50">
                  <h4 className="text-sm font-bold text-[#35282C] mb-3">Aktivitas & Log Pasangan Terbaru</h4>
                  <ul className="space-y-2.5 text-xs text-[#90777D]">
                    <li className="flex items-center justify-between">
                      <span>💍 James menandai <strong>"Pelunasan DP Gedung Graha Mahardika"</strong></span>
                      <span className="text-[#B89768]">2 jam lalu</span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span>💌 Amelia menambahkan <strong>"14 Kerabat dari Bandung ke Tamu VIP"</strong></span>
                      <span className="text-[#B89768]">5 jam lalu</span>
                    </li>
                    <li className="flex items-center justify-between">
                      <span>🌸 Mengunggah 4 swatch warna moodboard <strong>"Dusty Rose Romantic"</strong></span>
                      <span className="text-[#B89768]">Kemarin</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Tab 2: Anggaran */}
            {activeTab === "tab-anggaran" && (
              <div className="p-6 sm:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-[#BE5366]/10">
                  <div>
                    <p className="text-xs text-[#90777D]">Ringkasan Finansial</p>
                    <h3 className="text-xl font-serif-title font-bold text-[#35282C]">Total Anggaran: Rp130.000.000</h3>
                  </div>
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#738A78]/20 text-[#738A78]">
                    ✓ Sisa Anggaran Aman: Rp43.600.000
                  </span>
                </div>
                <div className="space-y-4 text-xs sm:text-sm">
                  <div>
                    <div className="flex justify-between mb-1 font-semibold text-[#35282C]">
                      <span>Gedung & Dekorasi (Alokasi: Rp55.000.000)</span>
                      <span className="text-[#BE5366]">Realisasi: Rp52.500.000</span>
                    </div>
                    <div className="w-full bg-[#F9EFF0] h-2.5 rounded-full overflow-hidden">
                      <div className="bg-[#BE5366] h-2.5 rounded-full" style={{ width: "95%" }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1 font-semibold text-[#35282C]">
                      <span>Catering & Jamuan (Alokasi: Rp45.000.000)</span>
                      <span className="text-[#B89768]">Realisasi: Rp20.000.000 (DP Terbayar)</span>
                    </div>
                    <div className="w-full bg-[#F9EFF0] h-2.5 rounded-full overflow-hidden">
                      <div className="bg-[#D3B68C] h-2.5 rounded-full" style={{ width: "44%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Undangan Digital */}
            {activeTab === "tab-undangan" && (
              <div className="p-6 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#F9EFF0] text-[#BE5366]">Luxury Mobile Invitation</span>
                    <h3 className="text-2xl font-serif-title font-bold text-[#35282C]">Website Undangan Siap Disebar</h3>
                    <p className="text-xs sm:text-sm text-[#90777D] leading-relaxed">
                      Tamu undangan dapat membuka link personalisasi, menyalakan alunan musik romantis, menambahkan ke Google Calendar, serta mengonfirmasi kedatangan dengan satu klik.
                    </p>
                    <div className="p-3 bg-[#FFFCF9] rounded-xl border border-[#BE5366]/20 text-xs text-[#35282C]">
                      🔗 Link: <span className="font-mono text-[#BE5366]">weddly.id/inv/amelia-james</span>
                    </div>
                  </div>
                  <div className="mx-auto w-64 bg-white rounded-3xl p-3 border-4 border-[#35282C]/20 shadow-xl text-center space-y-3">
                    <div className="w-16 h-1 bg-[#35282C]/20 mx-auto rounded-full"></div>
                    <p className="font-serif-title text-base font-bold text-[#35282C] pt-2">The Wedding of</p>
                    <p className="font-serif-title text-xl font-bold text-[#BE5366] italic">Amelia & James</p>
                    <div className="w-full h-32 rounded-lg bg-[#F9EFF0] flex items-center justify-center text-xs text-[#90777D]">
                      [Foto Pre-Wedding HD]
                    </div>
                    <button className="w-full py-2 bg-[#BE5366] text-white text-xs rounded-full font-semibold shadow-sm">
                      Buka Undangan
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 4: Moodboard */}
            {activeTab === "tab-moodboard" && (
              <div className="p-6 sm:p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-serif-title font-bold text-[#35282C] mb-1">Palet Konsep: Botanical Luxury</h3>
                  <p className="text-xs text-[#90777D]">Harmoni keanggunan warna alam tropis dipadu sentuhan mawar lembut dan emas sampanye.</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-3 rounded-2xl bg-white border border-[#BE5366]/20 shadow-sm text-center">
                    <div className="h-16 rounded-xl bg-[#BE5366] mb-2"></div>
                    <p className="text-xs font-bold text-[#35282C]">Dusty Rose</p>
                    <p className="text-[10px] font-mono text-[#90777D]">#BE5366</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-white border border-[#BE5366]/20 shadow-sm text-center">
                    <div className="h-16 rounded-xl bg-[#D3B68C] mb-2"></div>
                    <p className="text-xs font-bold text-[#35282C]">Champagne Gold</p>
                    <p className="text-[10px] font-mono text-[#90777D]">#D3B68C</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-white border border-[#BE5366]/20 shadow-sm text-center">
                    <div className="h-16 rounded-xl bg-[#738A78] mb-2"></div>
                    <p className="text-xs font-bold text-[#35282C]">Sage Green</p>
                    <p className="text-[10px] font-mono text-[#90777D]">#738A78</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-white border border-[#BE5366]/20 shadow-sm text-center">
                    <div className="h-16 rounded-xl bg-[#35282C] mb-2"></div>
                    <p className="text-xs font-bold text-[#35282C]">Deep Espresso</p>
                    <p className="text-[10px] font-mono text-[#90777D]">#35282C</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 5: Checklist */}
            {activeTab === "tab-checklist" && (
              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-[#BE5366]/10">
                  <h3 className="text-lg font-serif-title font-bold text-[#35282C]">Checklist Persiapan Menuju H-Day</h3>
                  <span className="text-xs font-bold text-[#738A78]">14 Selesai / 18 Item</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F9EFF0]/30 border border-[#BE5366]/10">
                    <span className="w-4 h-4 rounded bg-[#BE5366] text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                    <span className="text-xs sm:text-sm text-[#35282C] line-through">Booking venue utama & uang muka (DP)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F9EFF0]/30 border border-[#BE5366]/10">
                    <span className="w-4 h-4 rounded bg-[#BE5366] text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                    <span className="text-xs sm:text-sm text-[#35282C] line-through">Uji cita rasa catering bersama keluarga (Food Tasting)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#D3B68C]/40 shadow-sm">
                    <span className="w-4 h-4 rounded border border-[#BE5366] inline-block"></span>
                    <span className="text-xs sm:text-sm text-[#35282C] font-semibold">Fitting busana pengantin tahap akhir (Deadline: H-30 Hari)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 6: Honeymoon Planner */}
            {activeTab === "tab-honeymoon" && (
              <div className="p-6 sm:p-8 space-y-6">
                <div className="flex justify-between items-center pb-3 border-b border-[#BE5366]/10">
                  <div>
                    <h3 className="text-lg font-serif-title font-bold text-[#35282C]">Honeymoon Trip Planner</h3>
                    <p className="text-xs text-[#90777D]">Bali Romantic Getaway • 5 Hari 4 Malam</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#738A78] text-white">Target Tabungan: 85% Terpenuhi</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-2xl bg-[#F9EFF0]/40 border border-[#BE5366]/20">
                    <p className="text-[11px] text-[#90777D] font-semibold uppercase tracking-wider">Akomodasi & Villa</p>
                    <p className="text-base font-bold text-[#35282C] mt-1">Ubud Private Pool Villa</p>
                    <p className="text-xs text-[#738A78] font-bold mt-2">✓ Tiket & Booking Confirmed</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#F9EFF0]/40 border border-[#BE5366]/20">
                    <p className="text-[11px] text-[#90777D] font-semibold uppercase tracking-wider">Anggaran Perjalanan</p>
                    <p className="text-base font-bold text-[#35282C] mt-1">Rp18.500.000</p>
                    <p className="text-xs text-[#BE5366] font-bold mt-2">Terpenuhi via Honeymoon Fund</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#F9EFF0]/40 border border-[#BE5366]/20">
                    <p className="text-[11px] text-[#90777D] font-semibold uppercase tracking-wider">Itinerary & Aktivitas</p>
                    <p className="text-base font-bold text-[#35282C] mt-1">Sunset Cruise & Spa</p>
                    <p className="text-xs text-[#90777D] font-medium mt-2">6 Jadwal Rencana Disusun</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ==================== 7. WEDDLY AI FEATURE ==================== */}
      <section className="py-24 bg-white border-t border-[#BE5366]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F9EFF0] border border-[#BE5366]/20 text-xs font-bold text-[#9E4053]">
                <span>✦</span> Powered by Weddly Intelligent Engine
              </div>
              <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] leading-tight">
                Your Personal Wedding Planning Muse.
              </h2>
              <p className="text-base text-[#90777D] leading-relaxed">
                Butuh ide konsep pernikahan? Weddly AI siap membantu mengembangkan inspirasi dan memberikan saran untuk persiapan hari bahagiamu secara instan, relevan, dan terukur.
              </p>
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#BE5366]/20 text-[#BE5366] flex items-center justify-center text-xs mt-0.5 font-bold">✓</span>
                  <p className="text-xs sm:text-sm text-[#35282C]">Saran breakdown anggaran proporsional sesuai standar vendor Indonesia.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#BE5366]/20 text-[#BE5366] flex items-center justify-center text-xs mt-0.5 font-bold">✓</span>
                  <p className="text-xs sm:text-sm text-[#35282C]">Generasi susunan rundown akad nikah dan pesta resepsi menit-ke-menit.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl p-6 sm:p-8 bg-[#FFFCF9] border border-[#D3B68C]/40 shadow-luxury space-y-4">
                <div className="flex items-center justify-between pb-4 border-b border-[#D3B68C]/20">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#BE5366] text-white flex items-center justify-center text-xs font-bold">
                      ✦
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#35282C]">Weddly AI Assistant</p>
                      <p className="text-[10px] text-[#738A78]">Online • Siap Membantu</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono bg-white px-2.5 py-1 rounded-md border border-[#D3B68C]/20 text-[#90777D]">Mode: Konsep & Rundown</span>
                </div>

                <div className="flex justify-end">
                  <div className="max-w-md p-4 rounded-2xl rounded-tr-none bg-[#BE5366] text-white text-xs sm:text-sm leading-relaxed shadow-sm">
                    "Beri ide tema intimate garden party di Bandung dengan palet Sage Green & Champagne Gold untuk 150 tamu."
                  </div>
                </div>

                <div className="flex justify-start">
                  <div className="max-w-lg p-5 rounded-2xl rounded-tl-none bg-white border border-[#D3B68C]/30 text-xs sm:text-sm text-[#35282C] space-y-3 shadow-sm">
                    <p className="font-bold text-[#BE5366]">✨ Konsep Rekomendasi: "Botanical Romance at Dusk"</p>
                    <p className="text-[#90777D] leading-relaxed">
                      Tentu! Untuk 150 tamu di kawasan sejuk Bandung, konsep intimate garden party ini akan sangat syahdu:
                    </p>
                    <div className="p-3 bg-[#F9EFF0]/60 rounded-xl space-y-1.5 text-xs text-[#35282C]">
                      <p>🌿 <strong>Dekorasi:</strong> Kanopi fairy lights hangat, meja kayu rustic panjang, sentuhan lili putih & eucalyptus.</p>
                      <p>🍷 <strong>Catering:</strong> Semi-buffet dengan welcome canapé & mocktail apel kayu manis khas cuaca sejuk.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 8. HOW IT WORKS ==================== */}
      <section className="py-24 bg-[#F9EFF0]/20" id="cara-kerja">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#B89768]">Alur Praktis</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Mulai Rencanakan Pernikahanmu dalam 3 Langkah.
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              Proses cepat, praktis, dan langsung dapat digunakan bersama pasangan hari ini juga.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-white border border-[#D3B68C]/30 shadow-luxury hover:-translate-y-1 transition-all duration-300 relative">
              <div className="w-14 h-14 rounded-2xl bg-[#F9EFF0] border border-[#BE5366]/20 text-[#BE5366] flex items-center justify-center text-xl font-bold mb-6 font-serif-title">
                01
              </div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">Buat Akun Weddly</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">
                Daftar dalam 1 menit dan mulai uji coba gratis 14 hari tanpa biaya komitmen maupun keharusan memasukkan kartu kredit.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-white border border-[#D3B68C]/30 shadow-luxury hover:-translate-y-1 transition-all duration-300 relative">
              <div className="w-14 h-14 rounded-2xl bg-[#F9EFF0] border border-[#BE5366]/20 text-[#BE5366] flex items-center justify-center text-xl font-bold mb-6 font-serif-title">
                02
              </div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">Susun Rencana Pernikahan</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">
                Input tanggal pernikahan, tentukan target anggaran, dan sistem Weddly secara otomatis menyusun checklist & timeline persiapanmu.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-white border border-[#BE5366]/30 shadow-luxury hover:-translate-y-1 transition-all duration-300 relative">
              <div className="w-14 h-14 rounded-2xl bg-[#BE5366] text-white flex items-center justify-center text-xl font-bold mb-6 font-serif-title">
                03
              </div>
              <h3 className="font-serif-title text-2xl font-bold text-[#35282C] mb-3">Upgrade ke Premium</h3>
              <p className="text-sm text-[#90777D] leading-relaxed">
                Dapatkan akses penuh ke seluruh 12 modul lengkap selama 1 tahun penuh hanya dengan sekali bayar terjangkau.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 9. PRICING SECTION ==================== */}
      <section className="py-24 bg-white border-t border-[#BE5366]/10" id="harga">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#BE5366]">Investasi Hari Bahagia</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Pilihan Paket Perencanaan Transparan.
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              Tanpa biaya langganan berulang bulanan. Satu harga terjangkau untuk ketenangan persiapan pernikahanmu.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
            {/* Card 1: Trial */}
            <div className="p-8 sm:p-10 rounded-3xl bg-[#FFFCF9] border border-[#35282C]/10 flex flex-col justify-between hover:shadow-luxury transition-all">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif-title text-2xl font-bold text-[#35282C]">Weddly Trial</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#F9EFF0] text-[#90777D]">14 Hari</span>
                </div>
                <p className="text-xs sm:text-sm text-[#90777D] mb-6">
                  Coba pengalaman merencanakan pernikahan bersama Weddly sebelum memutuskan berlangganan.
                </p>
                <div className="mb-8">
                  <span className="text-4xl font-serif-title font-bold text-[#35282C]">Rp0</span>
                  <span className="text-xs text-[#90777D] ml-1">/ 14 hari masa uji</span>
                </div>
                <ul className="space-y-3.5 text-xs sm:text-sm text-[#35282C]">
                  <li className="flex items-center gap-3">
                    <span className="text-[#738A78] font-bold">✓</span> Akses dashboard dasar & checklist H-Day
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#738A78] font-bold">✓</span> Simulasi pencatatan anggaran dasar
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#738A78] font-bold">✓</span> Penyimpanan aman cloud selama 14 hari
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#738A78] font-bold">✓</span> Tanpa kartu kredit saat pendaftaran
                  </li>
                </ul>
              </div>
              <div className="pt-8">
                <button
                  onClick={handleStartTrial}
                  className="block w-full py-3.5 px-6 rounded-full text-center text-sm font-semibold text-[#35282C] border border-[#35282C]/30 hover:bg-[#F9EFF0] hover:border-[#BE5366] transition-all"
                >
                  Mulai Trial Gratis
                </button>
              </div>
            </div>

            {/* Card 2: Premium */}
            <div className="p-8 sm:p-10 rounded-3xl bg-[#FFFCF9] border-2 border-[#D3B68C] shadow-2xl relative flex flex-col justify-between overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#BE5366] text-white text-[11px] font-bold px-6 py-1.5 rounded-bl-2xl tracking-wider shadow-sm">
                LAUNCHING SPECIAL • HEMAT 59%
              </div>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif-title text-3xl font-bold text-[#35282C]">WEDDLY PREMIUM</h3>
                </div>
                <p className="text-xs sm:text-sm text-[#90777D] mb-6">
                  Akses penuh tanpa batas untuk merencanakan pernikahan impian dengan tenang sampai hari H tiba.
                </p>
                <div className="mb-6">
                  <div className="text-xs text-[#90777D] line-through font-semibold mb-1">Rp110.000</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-serif-title font-bold text-[#BE5366]">Rp45.000</span>
                    <span className="text-xs text-[#90777D]">/ 1 Tahun Penuh (365 Hari)</span>
                  </div>
                  <p className="text-[11px] text-[#B89768] font-semibold mt-1">Sekali bayar. Tanpa perpanjangan otomatis.</p>
                </div>
                <ul className="space-y-3 text-xs sm:text-sm text-[#35282C]">
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Akses penuh keseluruh Modul
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Undangan Digital
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Wedding Moodboard
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Weddly AI Assistant
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Pengelolaan keuangan, Tabungan, & Buku Tamu
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Honeymoon Planner
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Dokumen & Legalitas
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Seserahan & Mahar
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-[#BE5366] font-bold">✓</span> Cloud storage aman
                  </li>
                </ul>
              </div>
              <div className="pt-8 space-y-3">
                <button
                  onClick={handleBuyPremium}
                  className="block w-full py-4 px-6 rounded-full text-center text-sm font-bold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-floating transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Dapatkan Premium Sekarang
                </button>
                <p className="text-[11px] text-center text-[#90777D]">
                  ✓ Pembayaran instan via Transfer Bank / QRIS diverifikasi aman oleh tim Weddly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 10. FAQ ACCORDION ==================== */}
      <section className="py-24 bg-[#F9EFF0]/20" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#BE5366]">Bantuan Calon Pengantin</span>
            <h2 className="font-serif-title text-3xl sm:text-4xl lg:text-5xl font-bold text-[#35282C] mt-2">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="text-base text-[#90777D] mt-3">
              Temukan jawaban seputar keanggotaan, trial, keamanan data, dan fitur aplikasi Weddly.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { id: 1, q: "1. Apa itu Weddly?", a: "Weddly adalah platform web aplikasi all-in-one yang dirancang untuk memudahkan calon pengantin di Indonesia merencanakan seluruh tahapan pernikahan—mulai dari checklist, budgeting, moodboard visual, rundown, hingga undangan digital dalam satu tempat yang tersinkronisasi." },
              { id: 2, q: "2. Apakah Weddly bisa dicoba secara gratis?", a: "Ya! Kamu bisa mendaftar dan mencoba Weddly secara cuma-cuma melalui paket Weddly Trial selama 14 hari tanpa dipungut biaya dan tanpa perlu kartu kredit." },
              { id: 3, q: "3. Berapa lama masa trial berlangsung?", a: "Masa trial berlangsung selama 14 hari penuh terhitung sejak hari pertama akun didaftarkan." },
              { id: 4, q: "4. Apakah seluruh fitur tersedia saat trial?", a: "Saat masa trial, kamu mendapatkan akses ke modul dasar seperti checklist, ikhtisar anggaran awal, dan dashboard. Untuk fitur premium lanjutan seperti export file PDF/Excel, AI tanpa batas, dan undangan digital online publik, kamu dapat meng-upgrade ke paket Premium." },
              { id: 5, q: "5. Bagaimana cara membeli Weddly Premium?", a: "Cukup klik tombol 'Dapatkan Premium Sekarang', lengkapi detail namamu, dan lakukan pembayaran instan melalui QRIS atau Transfer Bank. Akses aktif dalam hitungan detik setelah diverifikasi admin." },
              { id: 6, q: "6. Apakah Rp45.000 merupakan biaya bulanan?", a: "Tidak. Rp45.000 adalah biaya sekali bayar untuk akses penuh selama 1 tahun (365 hari). Tidak ada tagihan terselubung ataupun perpanjangan otomatis." },
              { id: 7, q: "7. Apa yang terjadi setelah masa Premium berakhir?", a: "Setelah 1 tahun (yang biasanya pernikahan Anda telah selesai digelar), akun Anda akan berubah menjadi mode arsip hanya-baca (read-only) sehingga memori indah dan catatan pengeluaran tetap dapat Anda lihat kapan saja." },
              { id: 8, q: "8. Apakah data saya akan hilang setelah akses berakhir?", a: "Tidak. Seluruh data daftar tamu, kontrak vendor, dan rincian anggaran tetap tersimpan aman di server terenkripsi Weddly dan tidak akan dihapus sembarangan." },
              { id: 9, q: "9. Apakah Weddly bisa digunakan melalui smartphone?", a: "Sangat bisa. Weddly dirancang dengan prinsip Responsive Web Mobile-First sehingga sangat nyaman dibuka melalui browser Safari iPhone, Chrome Android, maupun tablet dan laptop." },
              { id: 10, q: "10. Apakah saya bisa upgrade sebelum trial berakhir?", a: "Tentu saja! Anda bisa langsung melakukan upgrade kapan pun, dan masa aktif 365 hari akan langsung diakumulasikan ke akun Anda." },
            ].map((faq) => {
              const isOpen = openFaqId === faq.id;
              return (
                <div key={faq.id} className="rounded-2xl bg-white border border-[#D3B68C]/30 overflow-hidden transition-all">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full p-6 text-left flex justify-between items-center focus:outline-none"
                  >
                    <span className="font-semibold text-sm sm:text-base text-[#35282C]">{faq.q}</span>
                    <span className="text-[#BE5366] font-bold text-xl ml-4">{isOpen ? "−" : "+"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 text-xs sm:text-sm text-[#90777D] leading-relaxed border-t border-[#F9EFF0] pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== 11. FINAL CALL TO ACTION ==================== */}
      <section className="relative py-28 lg:py-36 overflow-hidden flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <img
            alt="Romantic Bali Wedding"
            className="w-full h-full object-cover filter brightness-[0.35] contrast-105"
            src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#35282C]/90 via-[#35282C]/60 to-[#9E4053]/40"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 text-white">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#D3B68C]">Mulai Langkah Pertamamu</span>
          <h2 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            Your Dream Wedding Starts Here.
          </h2>
          <p className="text-base sm:text-lg text-[#F9EFF0]/90 max-w-2xl mx-auto leading-relaxed font-light">
            Mulai perjalanan menuju hari bahagiamu dengan persiapan yang lebih mudah, rapi, dan menyenangkan bersama pasangan tercinta.
          </p>
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleStartTrial}
              className="w-full sm:w-auto px-9 py-4 rounded-full text-base font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-floating transition-all duration-200 transform hover:-translate-y-0.5"
            >
              Coba Gratis 14 Hari
            </button>
            <button
              onClick={handleBuyPremium}
              className="w-full sm:w-auto px-9 py-4 rounded-full text-base font-semibold text-[#FFFCF9] border border-white/40 hover:bg-white/10 hover:border-white transition-all duration-200"
            >
              Dapatkan Premium Rp45.000
            </button>
          </div>
          <p className="text-xs text-white/70 pt-2">
            Akses cepat • Tanpa syarat kartu kredit • Garansi persiapan lebih rapi
          </p>
        </div>
      </section>

      {/* ==================== 12. FOOTER ==================== */}
      <footer className="bg-[#35282C] text-[#F9EFF0]/80 pt-16 pb-12 border-t border-[#BE5366]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-white/10">
            <div className="md:col-span-5 space-y-4">
              <div className="flex items-center gap-3">
                <img
                  alt="Weddly Logo"
                  className="w-9 h-9 object-contain brightness-125"
                  src="/weddly-logo.png"
                />
                <span className="font-serif-title text-2xl font-bold text-white tracking-wide">Weddly</span>
              </div>
              <p className="text-xs font-semibold text-[#D3B68C] tracking-wide">
                Your Wedding, Beautifully Planned.
              </p>
              <p className="text-xs text-[#F9EFF0]/70 leading-relaxed max-w-sm">
                Semua Persiapan Pernikahan, Dalam Satu Tempat. Platform SaaS manajemen pernikahan modern paling lengkap dan terjangkau di Indonesia.
              </p>
            </div>

            <div className="md:col-span-2 space-y-3">
              <h4 className="text-xs uppercase tracking-wider font-bold text-white">Produk</h4>
              <ul className="space-y-2 text-xs">
                <li><a onClick={(e) => scrollToSection(e, "fitur")} className="hover:text-[#D3B68C] transition-colors cursor-pointer" href="#fitur">12 Modul Fitur</a></li>
                <li><a onClick={(e) => scrollToSection(e, "harga")} className="hover:text-[#D3B68C] transition-colors cursor-pointer" href="#harga">Harga Paket</a></li>
                <li><a onClick={(e) => scrollToSection(e, "cara-kerja")} className="hover:text-[#D3B68C] transition-colors cursor-pointer" href="#cara-kerja">Cara Kerja</a></li>
                <li><a onClick={(e) => scrollToSection(e, "beranda")} className="hover:text-[#D3B68C] transition-colors cursor-pointer" href="#beranda">Simulasi Live</a></li>
              </ul>
            </div>

            <div className="md:col-span-3 space-y-3">
              <h4 className="text-xs uppercase tracking-wider font-bold text-white">Bantuan & Kontak</h4>
              <ul className="space-y-2 text-xs">
                <li><a onClick={(e) => scrollToSection(e, "faq")} className="hover:text-[#D3B68C] transition-colors cursor-pointer" href="#faq">Pusat Bantuan (FAQ)</a></li>
                <li><a className="hover:text-[#D3B68C] transition-colors" href="https://wa.me/" target="_blank" rel="noreferrer">WhatsApp Concierge Support</a></li>
                <li><span className="text-[#F9EFF0]/60">Email: halo@weddly.id</span></li>
                <li><span className="text-[#F9EFF0]/60">Jam Operasional: 09:00 - 21:00 WIB</span></li>
              </ul>
            </div>

            <div className="md:col-span-2 space-y-3">
              <h4 className="text-xs uppercase tracking-wider font-bold text-white">Legal</h4>
              <ul className="space-y-2 text-xs">
                <li><a className="hover:text-[#D3B68C] transition-colors" href="#faq">Kebijakan Privasi</a></li>
                <li><a className="hover:text-[#D3B68C] transition-colors" href="#faq">Syarat & Ketentuan</a></li>
                <li><a className="hover:text-[#D3B68C] transition-colors" href="#faq">Keamanan Data</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#F9EFF0]/60 gap-4">
            <p>© Weddly Indonesia. All Rights Reserved.</p>
            <p className="text-[11px]">Dirancang dengan cinta untuk calon pengantin di seluruh nusantara 🇮🇩</p>
          </div>
        </div>
      </footer>

      {/* Premium Checkout Payment Modal */}
      <PremiumPaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
      />
    </div>
  );
}
